'use client';
import { Builder } from '@builder.io/react';
import '@builder.io/widgets/dist/lib/builder-widgets-async';
// General
import { Carousel, carouselOptions } from 'components/global/carousel';
import { CustomImage } from './components/builder/components/4p-image';
import { customImageConfig } from './components/builder/components/4p-image.config';
import { CQLSection } from './components/builder/components/cql-section';
import { cqlSectionOptions } from './components/builder/components/cql-section.config';
import { CQLText } from './components/builder/components/cql-text';
import { builderTextOptions, cqlTextOptions } from './components/builder/components/cql-text.config';
import { withTimer, withTimerContext } from './components/builder/hooks/with-timer';
import { AddToCartCTA } from './components/global/add-to-cart-cta';
import { BackToTop } from './components/global/back-to-top';
import { CleanCode } from './components/global/clean-code';
import { SocialProof } from "./components/global/social-proof";
import { StickyUtilityWithSocialProof } from "./components/global/sticky-utility-with-social-proof";
import { Accordion } from './components/layout/accordion';
import { Tabs } from './components/layout/tabs';
//Header
import { HeaderAccount } from './components/header/header-account';
import { HeaderCart } from './components/header/header-cart';
import { HeaderWishlist } from './components/header/header-wishlist';
import { MegaNav } from './components/header/mega-nav';
import { MobileNav } from './components/header/mobile-nav';
// Product
import { Autocomplete } from 'components/searchspring/Autocomplete/Autocomplete';
import { MegaMenuRecsWrap } from 'components/searchspring/Megamenu/megaMenuRecs';
import { Recommendations } from 'components/searchspring/Recs/Recs';
// Global Product Card
import { Chat } from 'components/global/snap-chat';
import { ContentRecsWrap } from 'components/searchspring/ContentRecs/contentRecs';
import { ContentRecsStackedWrap } from 'components/searchspring/ContentRecs/contentRecsStacked';
import { VideoEmbed } from 'components/video/video-block';
// Funnel Components
import { VariantAccordion } from 'components/funnel/variant-accordion';
import { GladlyFaq } from 'components/global/gladly-faq';
import { StickyCTA } from 'components/global/sticky-cta';
import { VideoTimer, videoTimerOptions } from './components/funnel/video-timer';


Builder.registerComponent(VideoTimer, videoTimerOptions);

Builder.registerComponent(CustomImage, customImageConfig);


// Register this component for use in the Visual Editor
Builder.registerComponent(VideoEmbed, {
  name: 'CQL Video',
  friendlyName: 'CQL Video',
  inputs: [
    {
      name: 'url',
      type: 'url',
      defaultValue: 'Your link',
      required: true,
      onChange: (options: Map<string, any>) => {
        const url = options.get('url');

        if (url) {
          options.set('content', 'Loading...');
          const apiKey = process.env.I_FRAMELY_API_KEY
          const autoPlayUrl = `${url}?autoplay=1`;
          return fetch(`https://iframe.ly/api/iframely?url=${autoPlayUrl}&api_key=${apiKey}`)
            .then(res => res.json())
            .then(data => {
              if (options.get('url') === url) {
                if (data.links.player) {
                  data.links.player.forEach((player: any) => {
                    if (player.href.includes('autoplay')) {
                      options.set('contentAutoplay', player.html);
                    } else if (!player.href.includes('autoplay')) {
                      options.set('content', player.html);
                    } else {
                      options.set('content', 'Invalid url, please try another');
                    }
                  })
                }
              }
            })
            .catch(err => {
              options.set(
                'content',
                'There was an error embedding this URL, please try again or another URL'
              );
            });
        } else {
          options.delete('content');
        }
      }
    },
    {
      name: 'content',
      type: 'html',
      defaultValue: `<div style="padding: 20px; text-align: center">(Choose an embed URL)<div>`,
      hideFromUI: true,
    },
    {
      name: 'contentAutoplay',
      type: 'html',
      defaultValue: `<div style="padding: 20px; text-align: center">(Choose an embed URL)<div>`,
      hideFromUI: true,
    },
    {
      name: 'posterImage',
      friendlyName: 'Poster Image',
      helperText: "If no poster image is chosen then the video's YouTube poster will be used",
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
      defaultValue: 'https://cdn.builder.io/api/v1/image/assets%2F1adbd5971dac4273891f273603729777%2F150cebebb11f471995d81c043686e357',
      required: false,
    },
    {
      name: 'posterImageVisibility',
      friendlyName: 'Hide Poster Image',
      type: 'boolean',
      required: false,
    },
    {
      name: 'posterImageAltText',
      friendlyName: 'Poster Image Alt Text',
      type: 'text',
      required: false,
    },
    {
      name: 'lazy',
      type: 'boolean',
      advanced: true,
      defaultValue: true,
      helperText:
          'Lazy loading should be set to false for above-the-fold poster images.',
    },
    {
      name: 'fetchPriority',
      type: 'text',
      enum: ['high', 'low', 'auto'],
      advanced: true,
      defaultValue: 'auto',
      showIf: (options: any) => options.get('lazy') === false,
      helperText: 'HIGH: Hero/LCP image/facade. LOW: Below-the-fold image/facade. AUTO: Browser default (recommended).'
    },
  ],
}
)

Builder.registerComponent(Autocomplete, {
    name: 'Autocomplete',
  }
)

Builder.registerComponent(Recommendations, {
    name: 'Recommendations',
    inputs: [
      {
        name: 'profile',
        type: 'string',
        helperText: 'Searchspring Profile Tag for personalized recommendations. The profile id associated with a recommendations profile in the Searchspring Management Console. (Supports Comma-separated list, ex: "best-sellers,recently-viewed")'
      },
      {
        name: 'category',
        type: 'string',
        helperText: 'Searchspring Category Id for personalized recommendations. When using a Category Trending Recommendation Profile, the categories you want to recommend are required. (Supports Comma-separated list, ex: "210,220")'
      },
      {
        name: 'tileBanner',
        type: 'boolean',
        defaultValue: true,
      },
      {
        name: 'tileBurst',
        type: 'boolean',
        defaultValue: true,
      },
      {
        name: 'tileSpecs',
        type: 'boolean',
        defaultValue: true,
      },
      {
        name: 'tileTagline',
        type: 'boolean',
        defaultValue: true,
      },
      {
        name: 'tileRatings',
        type: 'boolean',
        defaultValue: true,
      },
      {
        name: 'tilePromo',
        type: 'boolean',
        defaultValue: true,
      },
      {
        name: 'tileATC',
        type: 'boolean',
        defaultValue: true,
      },
      {
        name: 'desktopSettings',
        type: 'object',
        defaultValue: {
          count: 4,
          per: 4,
        },
        subFields: [
          {
            name: 'count',
            friendlyName: 'Slides to Show',
            type: 'number',
            defaultValue: 4
          },
          {
            name: 'per',
            friendlyName: 'Slides to Scroll',
            type: 'number',
            defaultValue: 4
          },
        ]
      },
      {
        name: 'tabletSettings',
        type: 'object',
        defaultValue: {
          count: 2,
          per: 2,
        },
        subFields: [
          {
            name: 'count',
            friendlyName: 'Slides to Show',
            type: 'number',
            defaultValue: 4
          },
          {
            name: 'per',
            friendlyName: 'Slides to Scroll',
            type: 'number',
            defaultValue: 4
          },
        ]
      },
      {
        name: 'mobileSettings',
        type: 'object',
        defaultValue: {
          count: 1,
          per: 1,
        },
        subFields: [
          {
            name: 'count',
            friendlyName: 'Slides to Show',
            type: 'number',
            defaultValue: 4
          },
          {
            name: 'per',
            friendlyName: 'Slides to Scroll',
            type: 'number',
            defaultValue: 4
          },
        ]
      },
      {
        name: 'navTheme',
        friendlyName: 'Nav Theme',
        type: 'text',
        enum: [
          'light',
          'dark'
        ],
        defaultValue: 'dark'
      },
      {
        name: 'showNav',
        friendlyName: 'Show Navigation',
        type: 'boolean',
        defaultValue: true
      },
      {
        name: 'dtSwipe',
        friendlyName: 'Desktop Swipe',
        type: 'boolean',
        defaultValue: false
      },
      {
        name: 'autoPlay',
        friendlyName: 'AutoPlay',
        type: 'boolean',
        defaultValue: false
      },
      {
        name: 'speed',
        friendlyName: 'Speed (ms)',
        type: 'number',
        defaultValue: 500
      },
      {
        name: 'initialSlide',
        friendlyName: 'Index of first slide',
        type: 'number',
        defaultValue: 1
      },
    ]
  }
)

Builder.registerComponent(MegaMenuRecsWrap, {
  name: 'Megamenu Recs',
  inputs: [
    {
      name: 'profile',
      type: 'string',
      helperText: 'Searchspring Profile Tag for personalized recommendations. The profile id associated with a recommendations profile in the Searchspring Management Console. (Supports Comma-separated list, ex: "best-sellers,recently-viewed")'
    },
    {
      name: 'category',
      type: 'string',
      helperText: 'Searchspring Category Id for personalized recommendations. When using a Category Trending Recommendation Profile, the categories you want to recommend are required. (Supports Comma-separated list, ex: "210,220")'
    },
    {
      name: 'maxTiles',
      type: 'number',
      defaultValue: 4,
    },
    {
      name: 'tileBanner',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'tileBurst',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'tileSpecs',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'tileTagline',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'tileRatings',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'tilePromo',
      type: 'boolean',
      defaultValue: false,
    },
  ]
}
)

Builder.registerComponent(ContentRecsWrap, {
  name: 'Content Recs',
  friendlyName: 'Searchspring Content Recs',
  inputs: [
    {
      name: 'profile',
      type: 'string',
      helperText: 'Searchspring Profile Tag for personalized recommendations. The profile id associated with a recommendations profile in the Searchspring Management Console. (Supports Comma-separated list, ex: "best-sellers,recently-viewed")'
    },
    {
      name: 'category',
      type: 'string',
      helperText: 'Searchspring Category Id for personalized recommendations. When using a Category Trending Recommendation Profile, the categories you want to recommend are required. (Supports Comma-separated list, ex: "210,220")'
    },
    {
      name: 'maxTiles',
      type: 'number',
      defaultValue: 3,
    },
    {
      name: 'tileType',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'tileDate',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'tileDescription',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'tileTopics',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'desktopSettings',
      type: 'object',
      defaultValue: {
        count: 4,
        per: 4,
      },
      subFields: [
        {
          name: 'count',
          friendlyName: 'Slides to Show',
          type: 'number',
          defaultValue: 4
        },
        {
          name: 'per',
          friendlyName: 'Slides to Scroll',
          type: 'number',
          defaultValue: 4
        },
      ]
    },
    {
      name: 'tabletSettings',
      type: 'object',
      defaultValue: {
        count: 2,
        per: 2,
      },
      subFields: [
        {
          name: 'count',
          friendlyName: 'Slides to Show',
          type: 'number',
          defaultValue: 4
        },
        {
          name: 'per',
          friendlyName: 'Slides to Scroll',
          type: 'number',
          defaultValue: 4
        },
      ]
    },
    {
      name: 'mobileSettings',
      type: 'object',
      defaultValue: {
        count: 1,
        per: 1,
      },
      subFields: [
        {
          name: 'count',
          friendlyName: 'Slides to Show',
          type: 'number',
          defaultValue: 4
        },
        {
          name: 'per',
          friendlyName: 'Slides to Scroll',
          type: 'number',
          defaultValue: 4
        },
      ]
    },
    {
      name: 'navTheme',
      friendlyName: 'Nav Theme',
      type: 'text',
      enum: [
        'light',
        'dark'
      ],
      defaultValue: 'dark'
    },
    {
      name: 'showNav',
      friendlyName: 'Show Navigation',
      type: 'boolean',
      defaultValue: true
    },
    {
      name: 'dtSwipe',
      friendlyName: 'Desktop Swipe',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'autoPlay',
      friendlyName: 'AutoPlay',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'speed',
      friendlyName: 'Speed (ms)',
      type: 'number',
      defaultValue: 500
    },
    {
      name: 'initialSlide',
      friendlyName: 'Index of first slide',
      type: 'number',
      defaultValue: 1
    },
  ]
}
)

Builder.registerComponent(ContentRecsStackedWrap, {
  name: 'Content Recs Stacked',
  inputs: [
    {
      name: 'profile',
      type: 'string',
      helperText: 'Searchspring Profile Tag for personalized recommendations. The profile id associated with a recommendations profile in the Searchspring Management Console. (Supports Comma-separated list, ex: "best-sellers,recently-viewed")'
    },
    {
      name: 'category',
      type: 'string',
      helperText: 'Searchspring Category Id for personalized recommendations. When using a Category Trending Recommendation Profile, the categories you want to recommend are required. (Supports Comma-separated list, ex: "210,220")'
    },
    {
      name: 'tileType',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'tileDate',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'tileDescription',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'tileTopics',
      type: 'boolean',
      defaultValue: true,
    }
  ]
}
)

// ------------------
// General Components
// ------------------
Builder.registerComponent(withTimerContext(CQLSection), cqlSectionOptions);
Builder.registerComponent(withTimer(CQLText), cqlTextOptions);
Builder.registerComponent(withTimer(CQLText), builderTextOptions);

Builder.registerComponent(BackToTop, {
    name: 'Back to Top',
    noWrap: true,
    models: ['sticky-utility-menu', 'funnel-landing', 'funnel-offer'],
    image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/inpicture.svg',
    description: 'Global button for users to return to the top of the page',
    inputs: [
      {
        name: 'buttonPosition',
        friendlyName: 'Desktop Position',
        type: 'string',
        enum: [
          'Right',
          'Left'
        ],
        defaultValue: 'Right',
      },
    ],
});

Builder.registerComponent(SocialProof, {
  name: 'Social Proof Popup',
  noWrap: true,
  image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/inpicture.svg',
  description: 'Global component used to show previous customer\'s purchases',
  inputs: [
    {
      name: 'offerId',
      friendlyName: 'Offer ID of product you want displayed',
      type: 'number',
    },
  ],
});

Builder.registerComponent(StickyUtilityWithSocialProof, {
  name: 'Sticky Utility Menu With Social Proof',
  noWrap: true,
  image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/inpicture.svg',
  description: 'Sticky Utility Menu Incorporating Chat, Back To Top and Social Proof',
  defaultStyles: {
    marginTop: '0'
  },
  inputs: [
    {
      name: 'productSku',
      friendlyName: 'Product SKU',
      helperText: 'Product SKU to display in social proof. If left blank, defaults to main offer SKU.',
      type: 'string',
    },
  ],
});

Builder.registerComponent(Chat, {
  name: 'SnapEngage Chat',
  noWrap: true,
  models: ['sticky-utility-menu', 'funnel-landing', 'funnel-offer'],
  image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/inpicture.svg',
  description: 'Global button BC chat',
  inputs: [
    {
      name: 'buttonPosition',
      friendlyName: 'Desktop Position',
      type: 'string',
      enum: [
        'Right',
        'Left'
      ],
      defaultValue: 'Right',
    },
  ],
});

Builder.registerComponent(CleanCode, {
  name: 'Clean Code',
  noWrap: true,
  image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/code-slash.svg',
  description: 'Insert Code without Builder Junk',
  inputs: [
    {
      name: 'code',
      friendlyName: 'Clean Code',
      type: 'html',
    },
  ],
});

Builder.registerComponent(Carousel, carouselOptions);

Builder.registerComponent(Tabs, {
  name: 'Tabs',
  noWrap: true,
  image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/tab.svg',
  inputs: [
    {
      name: 'tabs',
      type: 'list',
      subFields: [
        {
          name: 'label',
          type: 'text',
          defaultValue: 'New tab',
        },
        {
          name: 'content',
          type: 'uiBlocks',
          defaultValue: [],
        },
      ],
      defaultValue: [
        {
          label: 'Tab 1',
          content: [],
        },
      ],
    },
    {
      name: 'tabsStyle',
      type: 'string',
      enum: ['Pill','Block'],
      defaultValue: 'Pill',
    },
  ],
});
Builder.registerComponent(Accordion, {
  name: 'Accordion',
  noWrap: true,
  image: 'https://cdn.builder.io/api/v1/image/assets%2FagZ9n5CUKRfbL9t6CaJOyVSK4Es2%2Ffab6c1fd3fe542408cbdec078bca7f35',
  override: true,
  inputs: [
    {
      name: 'folds',
      type: 'list',
      subFields: [
        {
          name: 'label',
          type: 'text',
          defaultValue: 'New fold',
        },
        {
          name: 'content',
          type: 'uiBlocks',
          defaultValue: [],
        },
      ],
      defaultValue: [
        {
          label: 'Fold 1',
          content: [],
        },
      ],
    },
    {
      name: 'accordionStyle',
      type: 'string',
      enum: ['Plus','Hybrid'],
      defaultValue: 'Plus',
    },
    {
      name: 'showFirstFoldActive',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Automatically show the first fold as open.  Disabling this setting will show all folds as initially closed (Not applicable to Hybrid Accordions).  ',
    }
  ],
});

Builder.registerComponent(GladlyFaq, {
  name: 'Gladly FAQ',
  description: 'Gladly FAQ Insert',
  noWrap: true,
  image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/chart.svg',
});


// ------------------
// Header Components
// ------------------
Builder.registerComponent(MegaNav,
  {
    name: 'Mega Nav',
    noWrap: true,
    image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/menu-boxed.svg',
    description: 'Mega Menu Navigation Container',
    inputs: [
      {
        name: 'nav',
        type: 'list',
        subFields: [
          {
            name: 'label',
            type: 'text',
            defaultValue: 'New Nav Item',
          },
          {
            name: 'link',
            type: 'text',
            defaultValue: '',
          },
          {
            name: 'textColorOverride',
            type: 'color',
            defaultValue: '',
          },
          {
            name: 'contentBackgroundColor',
            type: 'color',
            defaultValue: '#FFF',
          },
          {
            name: 'content',
            type: 'uiBlocks',
            defaultValue: [],
          },
        ],
        defaultValue: [
          {
            label: 'Nav 1',
            content: [],
          },
        ],
      },
      {
        name: 'sensitivity',
        type: 'number',
        defaultValue: 6,
        advanced: true,
        helperText: 'If the mouse travels fewer than this number of pixels between polling intervals, then the onMouseOver callback will be called. With the minimum sensitivity threshold of 1, the mouse must not move between polling intervals. With higher sensitivity thresholds you are more likely to receive a false positive.',
      },
      {
        name: 'interval',
        type: 'number',
        defaultValue: 100,
        advanced: true,
        helperText: 'The number of milliseconds hoverIntent waits between reading/comparing mouse coordinates. When the user`s mouse first enters the element its coordinates are recorded. The soonest the onMouseOut callback can be called is after a single polling interval. Setting the polling interval higher will increase the delay before the first possible onMouseOver call, but also increases the time to the next point of comparison.',
      },
      {
        name: 'timeout',
        type: 'number',
        defaultValue: 0,
        advanced: true,
        helperText: 'A simple delay, in milliseconds, before the onMouseOut callback is fired. If the user mouses back over the element before the timeout has expired the onMouseOut callback will not be called (nor will the onMouseOver callback be called). This is primarily to protect against sloppy/human mousing trajectories that temporarily (and unintentionally) take the user off of the target element... giving them time to return.',
      }
    ],
  }
);
Builder.registerComponent(MobileNav,
    {
      name: 'Mobile Nav',
      noWrap: true,
      image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/menu.svg',
      description: 'Mobile Menu Navigation Container',
      inputs: [
          {
            name: 'homeContent',
            type: 'uiBlocks',
            defaultValue: [],
          },
          {
            name: 'nav',
            type: 'list',
            subFields: [
              {
                name: 'label',
                type: 'text',
                defaultValue: 'New Nav Item',
              },
              {
                name: 'textColorOverride',
                type: 'color',
                defaultValue: '',
              },
              {
                name: 'contentBackgroundColor',
                type: 'color',
                defaultValue: '#FFF',
              },
              {
                name: 'content',
                type: 'uiBlocks',
                defaultValue: [],
              },
            ],
            defaultValue: [
              {
                label: 'Nav 1',
                content: [],
              },
            ],
          },
        ],
    }
  );
Builder.registerComponent(HeaderCart,
  {
    name: 'Cart Icon',
    noWrap: true,
    image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/shopping-cart.svg',
    description: 'Header Cart Icon'
  }
);
Builder.registerComponent(HeaderAccount,
  {
    name: 'Account Icon',
    noWrap: true,
    image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/user.svg',
    description: 'Header User Icon',
    inputs: [
        {
          name: 'showLabel',
          type: 'boolean',
          defaultValue: false,
        }
    ]
  }
);

Builder.registerComponent(HeaderWishlist,
  {
    name: 'Wishlist Icon',
    noWrap: true,
    image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/heart.svg',
    description: 'Header Heart Icon',
    inputs: [
        {
          name: 'showLabel',
          type: 'boolean',
          defaultValue: false,
        }
    ]
  }
);

Builder.register('insertMenu', {
    name: 'Header Components',
    items: [
      { name: 'Mega Nav' },
      { name: 'Mobile Nav' },
      { name: 'Cart Icon' },
      { name: 'Account Icon' },
      { name: 'Wishlist Icon' },
      { name: 'Autocomplete' },
      { name: 'Megamenu Recs'}
    ],
    priority: 1,
 });

// -------------------------
// Global Product Components
// -------------------------
Builder.registerComponent(AddToCartCTA,
  {
    name: 'addToCartCTA',
    friendlyName: 'General Add to Cart',
    image: 'https://cdn.builder.io/api/v1/image/assets/IsxPKMo2gPRRKeakUztj1D6uqed2/81a15681c3e74df09677dfc57a615b13',
    description: 'Add to Cart CTA for global use in Product Frame Symbol',
    inputs: [
      {
        name: 'setQuantity',
        friendlyName: 'Quantity',
        type: 'text',
        defaultValue: 1
      },
      {
        name: 'variantSku',
        friendlyName: 'Variant SKU',
        type: 'text',
        defaultValue: ''
      },
      {
        name: 'buttonColor',
        friendlyName: 'Button Style',
        type: 'string',
        enum: [
          'Primary',
          'Primary Inverted',
          'Secondary',
          'Secondary Inverted',
          '4P Orange 1',
          '4P Orange 2',
          '4P Green'
        ],
        defaultValue: 'Primary'
      },
      {
        name: 'buttonSize',
        friendlyName: 'Button Size',
        type: 'string',
        enum: [
          'Small',
          'Medium',
          'Large'
        ],
        defaultValue: 'Medium',
        helperText: 'Only Applies to 4P Styles'
      },
      {
        name: 'buttonEmbellish',
        friendlyName: 'Button Embellishment',
        type: 'string',
        enum: [
          'None',
          'Circle',
          'Arrows'
        ],
        defaultValue: 'None',
        helperText: 'Only Applies to 4P Styles'
      },
      {
        name: 'buttonText',
        friendlyName: 'Button Text',
        type: 'text',
        defaultValue: 'Add to Cart'
      },
      {
        name: 'buttonFontSize',
        friendlyName: 'Button Text Size',
        type: 'number'
      }
    ],
    defaults: {
      bindings: {
        'component.options.apiProduct': 'state.apiProduct?.sku ? state.apiProduct : state.product',
      }
    }
  }
);

// ------------------
// Funnel Components
// ------------------


Builder.registerComponent(StickyCTA, {
  name: 'StickyCTA',
  noWrap: true,
  image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/border-bottom.svg',
  override: true,
  inputs: [
    {
      name: 'marginTopDesktop',
      friendlyName: 'Margin Top for Desktops (number in px)',
      type: 'number',
      defaultValue: 0,
      min: 0,
    },
    {
      name: 'scrollThreshold',
      friendlyName: 'PX to scroll before this shows',
      type: 'number',
      defaultValue: 400,
      min: 0,
    },
    {
      name: 'content',
      type: 'uiBlocks',
      defaultValue: [],
    },
  ],
});

Builder.registerComponent(VariantAccordion, {
  name: 'Variant Accordion',
  noWrap: true,
  image: 'https://cdn.builder.io/api/v1/image/assets%2FagZ9n5CUKRfbL9t6CaJOyVSK4Es2%2Ffab6c1fd3fe542408cbdec078bca7f35',
  override: true,
  inputs: [
    {
      name: 'currentVariantSku',
      friendlyName: 'Current Variant SKU',
      type: 'string',
    },
    {
      name: 'folds',
      type: 'list',
      subFields: [
        {
          name: 'productSku',
          friendlyName: 'Product SKU',
          type: 'string',
          defaultValue: '810026145773-1P-1-1-1',
        },
        {
          name: 'alternateProductSku',
          friendlyName: 'Alternate Product SKU',
          type: 'string',
        },
        {
          name: 'freeShipping',
          friendlyName: 'Free Shipping?',
          type: 'boolean',
          defaultValue: false,
        },
        {
          name: 'foldTitle',
          friendlyName: 'Fold Title',
          type: 'CQLText',
        },
        {
          name: 'content',
          type: 'uiBlocks',
          defaultValue: [],
        },

      ],
      defaultValue: [
        {
          label: 'Fold 1',
          content: [],
        },
      ],
    }
  ],
  defaults: {
    bindings: {
      'component.options.currentVariantSku': 'state.funnelItem.sku'
    }
  }
});
